import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
  Alert,
  Badge,
  Row,
  Col,
} from "reactstrap";
import {
  Settings,
  CheckCircle,
  XCircle,
  Trash2,
  RefreshCw,
  Edit2,
  X,
} from "react-feather";
import {
  getBrandCrmAction,
  saveBrandCrmAction,
  saveBrandCrmResetAction,
  testBrandCrmAction,
  testBrandCrmResetAction,
  deleteBrandCrmAction,
  deleteBrandCrmResetAction,
} from "./store/actions";

const CrmIntegration = () => {
  const dispatch = useDispatch();

  const { getBrandCrmLoading, getBrandCrmData } = useSelector(
    (s) => s.getBrandCrmReducer,
  );
  const { saveBrandCrmLoading, saveBrandCrmData, saveBrandCrmError } =
    useSelector((s) => s.saveBrandCrmReducer);
  const { testBrandCrmLoading, testBrandCrmData, testBrandCrmError } =
    useSelector((s) => s.testBrandCrmReducer);
  const { deleteBrandCrmLoading, deleteBrandCrmData, deleteBrandCrmError } =
    useSelector((s) => s.deleteBrandCrmReducer);

  const [form, setForm] = useState({
    crm_driver: "nutshell",
    api_key: "",
    api_email: "",
    base_url: "",
  });
  const [isChangingKey, setIsChangingKey] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);

  // Unified save success/failure flags — covers both HTTP error and success:false in response body
  const saveSuccess = saveBrandCrmData && saveBrandCrmData.success;
  const saveFailed =
    saveBrandCrmError || (saveBrandCrmData && !saveBrandCrmData.success);
  const saveFailMsg = saveBrandCrmError || saveBrandCrmData?.msg;

  // Load on mount
  useEffect(() => {
    dispatch(getBrandCrmAction());
  }, []);

  // Pre-fill form whenever a fetch completes with valid data.
  // Fires on initial load, after save, and after delete+refetch.
  useEffect(() => {
    if (getBrandCrmLoading || !getBrandCrmData?.data) return;
    const d = getBrandCrmData.data;
    setForm({
      crm_driver: d.crm_driver || "nutshell",
      api_email: d.api_email || "",
      base_url: d.base_url || "",
      api_key: "",
    });
    // Open key input automatically when disconnected so user can re-enter key to reconnect
    setIsChangingKey(!d.is_active);
  }, [getBrandCrmLoading, getBrandCrmData]);

  // Reload after save (pre-fill effect above will fire and reset form + isChangingKey)
  useEffect(() => {
    if (saveSuccess || saveFailed) {
      dispatch(getBrandCrmAction());
      const t = setTimeout(() => dispatch(saveBrandCrmResetAction()), 5000);
      return () => clearTimeout(t);
    }
  }, [saveBrandCrmData, saveBrandCrmError, saveSuccess, saveFailed]);

  // Auto-dismiss test result
  useEffect(() => {
    if (testBrandCrmData || testBrandCrmError) {
      const t = setTimeout(() => dispatch(testBrandCrmResetAction()), 5000);
      return () => clearTimeout(t);
    }
  }, [testBrandCrmData, testBrandCrmError]);

  // Reload after delete
  useEffect(() => {
    if (deleteBrandCrmData?.success) {
      dispatch(getBrandCrmAction());
      setShowDeactivateConfirm(false);
      const t = setTimeout(() => dispatch(deleteBrandCrmResetAction()), 3000);
      return () => clearTimeout(t);
    }
  }, [deleteBrandCrmData]);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSave = (e) => {
    e.preventDefault();
    const payload = { crm_driver: form.crm_driver, api_email: form.api_email };
    // Send key when changing it OR when there is no existing config (initial setup)
    if ((isChangingKey || !existingConfig) && form.api_key)
      payload.api_key = form.api_key;
    if (form.base_url) payload.base_url = form.base_url;
    dispatch(saveBrandCrmAction(payload));
  };

  const handleTest = () => {
    const payload = { crm_driver: form.crm_driver, api_email: form.api_email };
    // Send key when changing it, initial setup, or no stored key yet
    if ((isChangingKey || !existingConfig) && form.api_key)
      payload.api_key = form.api_key;
    dispatch(testBrandCrmAction(payload));
  };

  const existingConfig = getBrandCrmData?.data;

  // Save button: disabled when nothing has changed
  const isDirty =
    !existingConfig ||
    !existingConfig.is_active || // always dirty when disconnected — user must re-save to reconnect
    form.crm_driver !== (existingConfig.crm_driver || "nutshell") ||
    form.api_email !== (existingConfig.api_email || "") ||
    form.base_url !== (existingConfig.base_url || "") ||
    (isChangingKey && form.api_key !== "");

  // Test button: needs email, plus either a saved key or a newly typed key
  const canTest =
    !!form.api_email &&
    (!!existingConfig?.api_key ||
      ((isChangingKey || !existingConfig) && !!form.api_key));

  // Unified test failure flag — covers both HTTP error and success:false in 200 body
  const testFailed =
    testBrandCrmError || (testBrandCrmData && !testBrandCrmData.success);
  const testFailMsg = testBrandCrmError || testBrandCrmData?.msg;



  return (
    <Fragment>
      <Card>
        <CardHeader className="border-bottom d-flex align-items-center">
          <Settings size={22} className="mr-1 text-primary" />
          <CardTitle tag="h4" className="mb-0">
            CRM Integration
          </CardTitle>
          {existingConfig && (
            <Badge
              color={
                existingConfig.is_active ? "light-success" : "light-danger"
              }
              className="ml-auto"
            >
              {existingConfig.is_active ? "Active" : "Inactive"}
            </Badge>
          )}
        </CardHeader>

        <CardBody>
          {/* Show full-page spinner only on the initial load (Redux store is empty on hard refresh) */}
          {getBrandCrmLoading && getBrandCrmData === null && (
            <div className="text-center my-3">
              <Spinner color="primary" />
            </div>
          )}

          {/* Save alerts */}
          {saveSuccess && (
            <Alert
              color="success"
              toggle={() => dispatch(saveBrandCrmResetAction())}
            >
              <div className="alert-body">{saveBrandCrmData.msg}</div>
            </Alert>
          )}
          {saveFailed && (
            <Alert
              color="danger"
              toggle={() => dispatch(saveBrandCrmResetAction())}
            >
              <div className="alert-body">{saveFailMsg}</div>
            </Alert>
          )}

          {/* Test alerts */}
          {testBrandCrmData?.success && (
            <Alert
              color="success"
              toggle={() => dispatch(testBrandCrmResetAction())}
            >
              <div className="alert-body">
                <CheckCircle size={16} className="mr-1" />
                {testBrandCrmData.msg}
              </div>
            </Alert>
          )}
          {testFailed && (
            <Alert
              color="danger"
              toggle={() => dispatch(testBrandCrmResetAction())}
            >
              <div className="alert-body">
                <XCircle size={16} className="mr-1" />
                {testFailMsg}
              </div>
            </Alert>
          )}

          {/* Delete alerts */}
          {deleteBrandCrmData?.success && (
            <Alert
              color="warning"
              toggle={() => dispatch(deleteBrandCrmResetAction())}
            >
              <div className="alert-body">{deleteBrandCrmData.msg}</div>
            </Alert>
          )}
          {deleteBrandCrmError && (
            <Alert
              color="danger"
              toggle={() => dispatch(deleteBrandCrmResetAction())}
            >
              <div className="alert-body">{deleteBrandCrmError}</div>
            </Alert>
          )}

          {/* Render form only after we have a response (prevents blank flash on hard reload) */}
          {(!getBrandCrmLoading || getBrandCrmData !== null) && <>
          <form onSubmit={handleSave}>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="crm_driver">CRM Platform</Label>
                  <Input
                    type="select"
                    id="crm_driver"
                    name="crm_driver"
                    value={form.crm_driver}
                    onChange={handleChange}
                  >
                    <option value="nutshell">Nutshell</option>
                  </Input>
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label for="api_email">API Email</Label>
                  <Input
                    type="email"
                    id="api_email"
                    name="api_email"
                    placeholder="you@example.com"
                    autoComplete="off"
                    value={form.api_email}
                    onChange={handleChange}
                  />
                </FormGroup>
              </Col>

              <Col md={12}>
                <FormGroup>
                  <Label>API Key</Label>
                  {!isChangingKey && existingConfig?.api_key ? (
                    /* Show masked value read-only with Edit icon button */
                    <div className="d-flex" style={{ gap: "6px" }}>
                      <Input
                        type="text"
                        value={existingConfig.api_key}
                        readOnly
                        style={{
                          background: "#f8f8f8",
                          letterSpacing: "0.1em",
                          color: "#6e6b7b",
                        }}
                      />
                      <Button
                        type="button"
                        color="outline-secondary"
                        title="Change API key"
                        onClick={() => setIsChangingKey(true)}
                        style={{ padding: "0.438rem 0.65rem" }}
                      >
                        <Edit2 size={14} />
                      </Button>
                    </div>
                  ) : (
                    /* Password input for new key entry */
                    <div className="d-flex" style={{ gap: "8px" }}>
                      <Input
                        type="password"
                        id="api_key"
                        name="api_key"
                        placeholder="Enter your API key"
                        value={form.api_key}
                        onChange={handleChange}
                        autoFocus={isChangingKey}
                      />
                      {existingConfig?.api_key && (
                        <Button
                          type="button"
                          color="outline-secondary"
                          title="Cancel key change"
                          onClick={() => {
                            setIsChangingKey(false);
                            setForm((f) => ({ ...f, api_key: "" }));
                          }}
                        >
                          <X size={14} />
                        </Button>
                      )}
                    </div>
                  )}
                </FormGroup>
              </Col>

              <Col md={12}>
                <FormGroup>
                  <Label for="base_url">
                    Base URL{" "}
                    <small className="text-muted">
                      (optional — Nutshell auto-discovers)
                    </small>
                  </Label>
                  <Input
                    type="text"
                    id="base_url"
                    name="base_url"
                    placeholder="https://app.nutshell.com/api/v1/json"
                    value={form.base_url}
                    onChange={handleChange}
                  />
                </FormGroup>
              </Col>
            </Row>

            <div className="d-flex flex-wrap" style={{ gap: "8px" }}>
              <Button
                color="primary"
                type="submit"
                disabled={saveBrandCrmLoading || !isDirty}
                title={!isDirty ? "No changes to save" : ""}
              >
                {saveBrandCrmLoading ? (
                  <Spinner size="sm" className="mr-1" />
                ) : null}
                {existingConfig && !existingConfig.is_active
                  ? "Save & Reconnect"
                  : "Save Changes"}
              </Button>

              <Button
                color="outline-secondary"
                type="button"
                onClick={handleTest}
                disabled={testBrandCrmLoading || !canTest}
                title={!canTest ? "Enter API Email to test" : ""}
              >
                {testBrandCrmLoading ? (
                  <Spinner size="sm" className="mr-1" />
                ) : (
                  <RefreshCw size={14} className="mr-1" />
                )}
                Test Connection
              </Button>

              {existingConfig?.is_active && (
                <Button
                  color="outline-danger"
                  type="button"
                  onClick={() => setShowDeactivateConfirm(true)}
                  disabled={deleteBrandCrmLoading}
                >
                  <Trash2 size={14} className="mr-1" />
                  Disconnect
                </Button>
              )}
            </div>
          </form>

          {/* Inline deactivate confirm */}
          {showDeactivateConfirm && (
            <Alert color="warning" className="mt-3">
              <div className="alert-body">
                <strong>Are you sure?</strong> This will deactivate your CRM
                integration. RD applicants will no longer be synced to your CRM
                until you reconnect.
                <div className="mt-2 d-flex" style={{ gap: "8px" }}>
                  <Button
                    color="danger"
                    size="sm"
                    onClick={() => dispatch(deleteBrandCrmAction())}
                    disabled={deleteBrandCrmLoading}
                  >
                    {deleteBrandCrmLoading ? (
                      <Spinner size="sm" className="mr-1" />
                    ) : null}
                    Yes, Disconnect
                  </Button>
                  <Button
                    color="secondary"
                    size="sm"
                    onClick={() => setShowDeactivateConfirm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </Alert>
          )}
          </>}
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <h6 className="text-muted mb-1">How it works</h6>
          <ul className="pl-2 mb-0" style={{ fontSize: "0.9rem" }}>
            <li>Save your CRM credentials above.</li>
            <li>
              Whenever an RD applies to one of your campaigns, their contact
              details are automatically synced to your CRM.
            </li>
            <li>
              For Nutshell: a contact is created/updated (tagged{" "}
              <code>rd-applicant</code>) and a lead is created for the campaign.
            </li>
            <li>
              Use <strong>Test Connection</strong> to verify your credentials
              before saving.
            </li>
            <li>
              Click <strong>Disconnect</strong> to pause syncing without
              deleting your saved config.
            </li>
          </ul>
        </CardBody>
      </Card>
    </Fragment>
  );
};

export default CrmIntegration;
